function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696
  // 705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

function ensureProductTestimonialsMenuItem() {
  const configs = [
    {
      containerSelector: '#HeaderMenu-shop + ul, #HeaderMenu-MenuList-3',
      linkClass:
        'header__menu-item list-menu__item link link--text focus-inset caption-large',
      itemWrapper: 'li',
      idPrefix: 'HeaderMenu-shop',
    },
    {
      containerSelector: '#HeaderDrawer-shop ~ div .menu-drawer__menu',
      linkClass: 'menu-drawer__menu-item link link--text list-menu__item focus-inset',
      itemWrapper: 'li',
      idPrefix: 'HeaderDrawer-shop',
    },
  ];

  configs.forEach(({ containerSelector, linkClass, itemWrapper, idPrefix }) => {
    document.querySelectorAll(containerSelector).forEach((container) => {
      if (!container || container.querySelector('[href="/pages/product-testimonials"]')) return;

      const item = document.createElement(itemWrapper);
      const link = document.createElement('a');
      link.id = `${idPrefix}-product-testimonials`;
      link.href = '/pages/product-testimonials';
      link.className = linkClass;
      link.textContent = 'Product testimonials';
      if (window.location.pathname === '/pages/product-testimonials') {
        link.setAttribute('aria-current', 'page');
      }
      item.appendChild(link);
      container.appendChild(item);
    });
  });
}

function normalizeTestimonialsNavigation() {
  const clinicSelectors = [
    '#HeaderMenu-clinic-testimonials',
    '#HeaderDrawer-clinic-testimonials',
  ];

  clinicSelectors.forEach((selector) => {
    document.querySelectorAll(selector).forEach((link) => {
      link.setAttribute('href', '/pages/testimonials');
    });
  });

  document.querySelectorAll('.balance-clinic-hero__note-link[href="/pages/patient-testimonials"]').forEach((link) => {
    link.setAttribute('href', '/pages/product-testimonials');

    const title = link.querySelector('.balance-clinic-hero__note-title');
    const text = link.querySelector('.balance-clinic-hero__note-text');

    if (title) title.textContent = 'Product Testimonials';
    if (text) {
      text.textContent =
        'See what patients experienced with Herbal Digestive Support and Calm Rest Formula.';
    }
  });
}

function redirectAppointmentShortlink() {
  if (window.location.pathname === '/appointment' || window.location.pathname === '/appointment/') {
    window.location.replace('/pages/book-appointment');
  }
}

function removeHomepageDuplicatePathSection() {
  if (window.location.pathname !== '/') return;

  const duplicateHeadings = [
    'Start with the clinic, then use stories and guides to decide with confidence.',
  ];

  document.querySelectorAll('.balance-care-collections__header h2').forEach((heading) => {
    const text = heading.textContent.trim();
    if (!duplicateHeadings.includes(text)) return;

    const section = heading.closest('section');
    if (section) {
      section.remove();
    }
  });
}

function repairHomepageSlideshow() {
  if (window.location.pathname !== '/') return;

  const shell = document.querySelector('.balance-home-slideshow__shell');
  if (!shell) return;

  const slides = Array.from(shell.querySelectorAll('.balance-home-slideshow__slide'));
  if (!slides.length) return;

  const needsRepair = slides.some((slide) => {
    const media = slide.querySelector('.balance-home-slideshow__media');
    if (!media) return false;
    return !media.querySelector('img') || /Liquid error/i.test(media.textContent || '');
  });

  if (!needsRepair) return;

  const slideData = [
    {
      eyebrow: 'Dr. Ping Wang, PhD, OMD, L.Ac. | UCLA East-West Medicine Clinician',
      heading: 'Acupuncture & Holistic Wellness in Santa Monica',
      body: 'Personalized acupuncture and Traditional Chinese Medicine care with Dr. Wang, supporting women’s wellness, fertility, pain relief, sleep, stress management, and healthy aging.',
      ctaLabel: 'Book Appointment',
      ctaLink: '/pages/book-appointment',
      image:
        'https://www.balancehealthcare.us/wp-content/uploads/2018/01/acupuncture3-2-e1722315088947.jpg',
      alt: 'Acupuncture treatment at Balance Healthcare in Santa Monica',
    },
    {
      heading: 'Women’s Wellness and Fertility Support',
      body: 'Traditional Chinese Medicine care that looks at cycles, stress, sleep, digestion, and the whole person.',
      ctaLabel: 'Explore Women’s Wellness',
      ctaLink: '/pages/womens-health-acupuncture-santa-monica',
      image: 'https://www.balancehealthcare.us/wp-content/uploads/2018/01/maibo-e1722315178346.jpg',
      alt: 'Traditional Chinese Medicine pulse diagnosis',
    },
    {
      heading: 'Pain, Sleep, Stress, and Digestion',
      body: 'We focus on symptom patterns that connect the body rather than treating each concern in isolation.',
      ctaLabel: 'View Services',
      ctaLink: '/pages/services',
      image: 'https://www.balancehealthcare.us/wp-content/uploads/2018/01/herb3-1-e1722315492325.jpg',
      alt: 'Chinese herbal medicine prepared at Balance Healthcare',
    },
    {
      heading: 'Personalized Care With Clear Next Steps',
      body: 'Acupuncture, herbal medicine, cupping, moxibustion, and supportive care are tailored to what you need.',
      ctaLabel: 'Meet Dr. Wang',
      ctaLink: '/pages/dr-ping-wang',
      image: 'https://www.balancehealthcare.us/wp-content/uploads/2018/01/tuina-e1722315406919.jpg',
      alt: 'Supportive care at Balance Healthcare',
    },
  ];

  slides.forEach((slide, index) => {
    const data = slideData[index];
    if (!data) return;

    const media = slide.querySelector('.balance-home-slideshow__media');
    const content = slide.querySelector('.balance-home-slideshow__content');
    let eyebrow = slide.querySelector('.balance-home-slideshow__eyebrow');
    const heading = slide.querySelector('.balance-home-slideshow__heading');
    const body = slide.querySelector('.balance-home-slideshow__body');
    const cta = slide.querySelector('.balance-home-slideshow__cta');

    if (media) {
      media.innerHTML = '';
      const image = document.createElement('img');
      image.src = data.image;
      image.alt = data.alt;
      image.loading = index === 0 ? 'eager' : 'lazy';
      media.appendChild(image);
    }

    if (content) {
      if (data.eyebrow) {
        if (!eyebrow) {
          eyebrow = document.createElement('p');
          eyebrow.className = 'balance-home-slideshow__eyebrow';
          content.insertBefore(eyebrow, heading || body || cta || content.firstChild);
        }
        eyebrow.textContent = data.eyebrow;
      } else if (eyebrow) {
        eyebrow.remove();
      }
    }

    if (heading) heading.textContent = data.heading;
    if (body) body.textContent = data.body;

    if (cta) {
      cta.textContent = data.ctaLabel;
      cta.setAttribute('href', data.ctaLink);
    }
  });
}

function ensureHomepageHeroEyebrow() {
  if (window.location.pathname !== '/') return;

  const firstSlide = document.querySelector('.balance-home-slideshow__slide');
  const content = firstSlide?.querySelector('.balance-home-slideshow__content');
  const heading = firstSlide?.querySelector('.balance-home-slideshow__heading');

  if (!content || !heading || content.querySelector('.balance-home-slideshow__eyebrow')) return;
  if (!heading.textContent.includes('Acupuncture & Holistic Wellness in Santa Monica')) return;

  const eyebrow = document.createElement('p');
  eyebrow.className = 'balance-home-slideshow__eyebrow';
  eyebrow.textContent = 'Dr. Ping Wang, PhD, OMD, L.Ac. | UCLA East-West Medicine Clinician';
  content.insertBefore(eyebrow, heading);
}

function refreshHairNewProductImage() {
  if (window.location.pathname !== '/products/hair-new-nutrition-for-hair-growth') return;

  const updatedImage =
    '//balance-healthcare.myshopify.com/cdn/shop/files/hair-new-v3.png?v=1775630608';

  document
    .querySelectorAll('img[src*="hair-new_"], img[src*="hair-new-v3"], img[srcset*="hair-new_"], img[srcset*="hair-new-v3"]')
    .forEach((image) => {
      image.src = `${updatedImage}&width=1445`;
      image.srcset = '';
      image.removeAttribute('srcset');
      image.alt = 'Hair New botanical product image';
    });

  document
    .querySelectorAll('meta[property="og:image"], meta[property="og:image:secure_url"], meta[name="twitter:image"]')
    .forEach((meta) => {
      meta.setAttribute('content', `https:${updatedImage}`);
    });
}

function injectTypographyNormalizationStyles() {
  if (document.getElementById('balance-type-normalization')) return;

  const style = document.createElement('style');
  style.id = 'balance-type-normalization';
  style.textContent = `
    body {
      font-size: 1.42rem !important;
    }

    p,
    li,
    .rte p,
    .rte li,
    .article-template__content p,
    .article-template__content li,
    .shopify-policy__body p,
    .shopify-policy__body li,
    .text-body,
    .caption,
    .caption-large,
    .card__caption,
    .field__label,
    .footer-block__details-content,
    .footer-block__details-content p,
    .footer-block__details-content li,
    .footer__content-bottom,
    .breadcrumbs__link,
    .breadcrumbs__item,
    .breadcrumb a,
    .balance-care-lanes__body,
    .balance-care-lanes__card-text,
    .balance-care-lanes__card-link,
    .balance-care-collections__body,
    .balance-care-collections__text,
    .balance-care-collections__link,
    .balance-proof-strip__body,
    .balance-proof-strip__text,
    .balance-product-support__body,
    .balance-product-support__text,
    .balance-collection-guide__body,
    .balance-collection-guide__card-text,
    .balance-collection-guide__card-link,
    .balance-clinic-access__body,
    .balance-clinic-access__detail-text,
    .balance-clinic-access__detail-link,
    .balance-clinic-gallery__body,
    .balance-clinic-gallery__text,
    .balance-conversion-band__body,
    .balance-home-hero__body,
    .balance-home-hero__trust-text,
    .balance-clinic-hero__body,
    .balance-clinic-hero__note-text,
    .balance-clinic-hero__media-caption,
    .balance-shop-faq__body,
    .balance-shop-faq__answer,
    .contact__lede {
      font-size: 1.42rem !important;
      line-height: 1.72 !important;
    }

    .header__menu-item span,
    .list-menu__item--link,
    .mega-menu__link,
    .header__active-menu-item,
    .button,
    .button--secondary,
    .shopify-payment-button__button,
    .price,
    .price__container,
    .price-item {
      font-size: 1.42rem !important;
    }

    .balance-home-hero__eyebrow,
    .balance-clinic-hero__eyebrow,
    .balance-care-lanes__eyebrow,
    .balance-care-collections__eyebrow,
    .balance-proof-strip__eyebrow,
    .balance-product-support__eyebrow,
    .balance-collection-guide__eyebrow,
    .balance-clinic-access__eyebrow,
    .balance-clinic-gallery__eyebrow,
    .balance-conversion-band__eyebrow,
    .balance-shop-faq__eyebrow {
      font-size: 1.02rem !important;
    }

    h1,
    .h0,
    .hxl {
      font-size: clamp(2.8rem, 4.5vw, 4.2rem) !important;
    }

    h2,
    .h1 {
      font-size: clamp(2.05rem, 2.8vw, 2.9rem) !important;
    }

    h3,
    .h2 {
      font-size: clamp(1.78rem, 2.15vw, 2.2rem) !important;
    }

    h4,
    .h3 {
      font-size: clamp(1.56rem, 1.55vw, 1.84rem) !important;
    }

    body .product__title h1 {
      font-size: clamp(2.4rem, 2.8vw, 3.4rem) !important;
      line-height: 1.1 !important;
    }

    body .product__text,
    body .product__inventory,
    body .product__tax,
    body .product__description p,
    body .product__description li,
    body .product-form__input .form__label,
    body .product__accordion .accordion__content {
      font-size: 1.42rem !important;
      line-height: 1.68 !important;
    }

    body .product__accordion .accordion__title,
    body .product__accordion .summary__title > .accordion__title,
    body .product__accordion summary .accordion__title.inline-richtext,
    body .product__accordion summary h2.accordion__title,
    body .product__accordion summary h2.h4.accordion__title {
      font-size: 1.72rem !important;
      line-height: 1.3 !important;
      font-weight: 400 !important;
    }

    .balance-card-product__subtitle {
      font-size: 1.2rem !important;
      line-height: 1.58 !important;
    }

    @media screen and (min-width: 990px) {
      body {
        font-size: 1.5rem !important;
      }

      p,
      li,
      .rte p,
      .rte li,
      .article-template__content p,
      .article-template__content li,
      .shopify-policy__body p,
      .shopify-policy__body li,
      .text-body,
      .caption,
      .caption-large,
      .card__caption,
      .field__label,
      .footer-block__details-content,
      .footer-block__details-content p,
      .footer-block__details-content li,
      .footer__content-bottom,
      .breadcrumbs__link,
      .breadcrumbs__item,
      .breadcrumb a,
      .balance-care-lanes__eyebrow,
      .balance-care-lanes__body,
      .balance-care-lanes__card-tag,
      .balance-care-lanes__card-text,
      .balance-care-lanes__card-link,
      .balance-care-collections__eyebrow,
      .balance-care-collections__body,
      .balance-care-collections__tag,
      .balance-care-collections__status,
      .balance-care-collections__text,
      .balance-care-collections__link,
      .balance-proof-strip__eyebrow,
      .balance-proof-strip__body,
      .balance-proof-strip__label,
      .balance-proof-strip__text,
      .balance-product-support__eyebrow,
      .balance-product-support__body,
      .balance-product-support__label,
      .balance-product-support__text,
      .balance-collection-guide__eyebrow,
      .balance-collection-guide__body,
      .balance-collection-guide__status-pill,
      .balance-collection-guide__status-text,
      .balance-collection-guide__card-label,
      .balance-collection-guide__card-text,
      .balance-collection-guide__card-link,
      .balance-clinic-access__eyebrow,
      .balance-clinic-access__body,
      .balance-clinic-access__detail-eyebrow,
      .balance-clinic-access__detail-text,
      .balance-clinic-access__detail-link,
      .balance-clinic-gallery__eyebrow,
      .balance-clinic-gallery__body,
      .balance-clinic-gallery__title,
      .balance-clinic-gallery__text,
      .balance-conversion-band__eyebrow,
      .balance-conversion-band__body,
      .balance-home-hero__eyebrow,
      .balance-home-hero__body,
      .balance-home-hero__trust-label,
      .balance-home-hero__trust-text,
      .balance-clinic-hero__eyebrow,
      .balance-clinic-hero__body,
      .balance-clinic-hero__media-badge,
      .balance-clinic-hero__media-caption,
      .balance-clinic-hero__note-title,
      .balance-clinic-hero__note-text,
      .balance-shop-faq__eyebrow,
      .balance-shop-faq__body,
      .balance-shop-faq__question,
      .balance-shop-faq__answer,
      .contact__lede {
        font-size: 1.5rem !important;
        line-height: 1.72 !important;
      }

      .header__menu-item span,
      .list-menu__item--link,
      .mega-menu__link,
      .header__active-menu-item {
        font-size: 1.5rem !important;
      }

      body .product__text,
      body .product__inventory,
      body .product__tax,
      body .product__description p,
      body .product__description li,
      body .product-form__input .form__label,
      body .product__accordion .accordion__content,
      body .price,
      body .price__container,
      body .price-item {
        font-size: 1.5rem !important;
        line-height: 1.68 !important;
      }

      body .product__accordion .accordion__title,
      body .product__accordion .summary__title > .accordion__title,
      body .product__accordion summary .accordion__title.inline-richtext,
      body .product__accordion summary h2.accordion__title,
      body .product__accordion summary h2.h4.accordion__title {
        font-size: 1.82rem !important;
      }

      body .price--large .price-item--regular,
      body .price--large .price-item--sale,
      .balance-card-product__subtitle {
        font-size: 1.46rem !important;
        line-height: 1.66 !important;
      }
    }
  `;

  document.head.appendChild(style);
}

function removeContactAccessHeader() {
  if (window.location.pathname !== '/pages/contact') return;

  document.querySelectorAll('.balance-clinic-access__header').forEach((header) => {
    const text = (header.textContent || '').replace(/\s+/g, ' ').trim();

    if (
      !text ||
      (text.includes('Clinic details') &&
        text.includes('Everything you need before your visit.') &&
        text.includes('Address, hours, insurance, and how to get here.'))
    ) {
      header.remove();
    }
  });
}

function normalizeFirstVisitGuideLayout() {
  const firstVisitPaths = [
    '/pages/your-first-acupuncture-visit',
    '/pages/your-first-acupuncture-visit-v2-temp',
  ];

  if (!firstVisitPaths.includes(window.location.pathname)) return;

  const firstStep = document.querySelector('.balance-fv-guide__step--with-map');
  if (!firstStep) return;

  const image = firstStep.querySelector('.balance-fv-guide__step-image');
  const stepContent = firstStep.querySelector('.balance-fv-guide__step-content');
  const map = firstStep.querySelector('.balance-fv-guide__map');

  if (!stepContent || !map) return;

  firstStep.classList.add('balance-fv-guide__step--map-lead');

  if (image) {
    image.remove();
  }

  if (map.parentElement === stepContent) {
    firstStep.insertBefore(map, stepContent);
  }
}

function swapFirstVisitGuideClinicPhotos() {
  const firstVisitPaths = [
    '/pages/your-first-acupuncture-visit',
    '/pages/your-first-acupuncture-visit-v2-temp',
  ];

  if (!firstVisitPaths.includes(window.location.pathname)) return;

  const heroImage = document.querySelector('.balance-article-page__hero img');
  const followupImage = document.querySelector('.balance-fv-guide__steps .balance-fv-guide__step:last-child .balance-fv-guide__step-image img');

  if (!heroImage || !followupImage) return;

  const heroSrc = heroImage.getAttribute('src') || '';
  const followupSrc = followupImage.getAttribute('src') || '';

  if (!heroSrc.includes('balance-clinic-front-desk-real') || !followupSrc.includes('balance-clinic-waiting-room-real')) {
    return;
  }

  heroImage.setAttribute('src', heroSrc.replace('balance-clinic-front-desk-real', 'balance-clinic-waiting-room-real'));
  heroImage.setAttribute('srcset', (heroImage.getAttribute('srcset') || '').replaceAll('balance-clinic-front-desk-real', 'balance-clinic-waiting-room-real'));

  followupImage.setAttribute('src', followupSrc.replace('balance-clinic-waiting-room-real', 'balance-clinic-front-desk-real'));
  followupImage.setAttribute('srcset', (followupImage.getAttribute('srcset') || '').replaceAll('balance-clinic-waiting-room-real', 'balance-clinic-front-desk-real'));

  if (!heroImage.getAttribute('alt')) {
    heroImage.setAttribute('alt', 'Balance Healthcare waiting room');
  }

  followupImage.setAttribute('alt', 'Balance Healthcare front desk and hallway');
}

async function hydrateFirstVisitGuideFromFreshPage() {
  if (window.location.pathname !== '/pages/your-first-acupuncture-visit') return;

  const intro = document.querySelector('.balance-article-page__intro');
  const steps = document.querySelector('.balance-fv-guide__steps');
  const staleIntro =
    intro && intro.textContent && intro.textContent.includes('What to expect at your first acupuncture appointment');
  const emptySteps = steps && steps.children.length === 0;

  if (!staleIntro && !emptySteps) return;

  try {
    const response = await fetch(`/pages/your-first-acupuncture-visit-v2-temp?cb=${Date.now()}`, {
      credentials: 'same-origin',
    });
    if (!response.ok) return;

    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const freshMain = doc.querySelector('#MainContent');
    const currentMain = document.querySelector('#MainContent');

    if (!freshMain || !currentMain) return;

    currentMain.innerHTML = freshMain.innerHTML;
    normalizeFirstVisitGuideLayout();
    swapFirstVisitGuideClinicPhotos();
  } catch (error) {
    console.warn('Could not hydrate first visit guide from fresh page.', error);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    normalizeTestimonialsNavigation();
    ensureProductTestimonialsMenuItem();
    removeHomepageDuplicatePathSection();
    repairHomepageSlideshow();
    refreshHairNewProductImage();
    injectTypographyNormalizationStyles();
    removeContactAccessHeader();
    hydrateFirstVisitGuideFromFreshPage();
    normalizeFirstVisitGuideLayout();
    swapFirstVisitGuideClinicPhotos();
  });
} else {
  normalizeTestimonialsNavigation();
  ensureProductTestimonialsMenuItem();
  removeHomepageDuplicatePathSection();
  repairHomepageSlideshow();
  refreshHairNewProductImage();
  injectTypographyNormalizationStyles();
  removeContactAccessHeader();
  hydrateFirstVisitGuideFromFreshPage();
  normalizeFirstVisitGuideLayout();
  swapFirstVisitGuideClinicPhotos();
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

function onBalanceDocumentReady(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
  } else {
    callback();
  }
}

function injectPatientSuccessStoriesStyles() {
  if (document.getElementById('balance-success-stories-style')) return;

  const style = document.createElement('style');
  style.id = 'balance-success-stories-style';
  style.textContent = `
    .balance-success-stories-title-hidden {
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    }

    .balance-success-stories-page {
      display: grid;
      gap: 2.4rem;
    }

    .balance-success-stories-hero {
      display: grid;
      gap: 1.4rem;
      padding: 2.4rem 2rem;
      border-radius: 2.8rem;
      background:
        radial-gradient(circle at top left, rgba(22, 183, 211, 0.12), transparent 34%),
        linear-gradient(180deg, #fffdfa 0%, #f8f5ef 100%);
      border: 1px solid rgba(23, 49, 47, 0.08);
      box-shadow: 0 20px 46px rgba(23, 49, 47, 0.06);
    }

    .balance-success-stories-hero::before {
      content: '';
      width: 8rem;
      height: 0.24rem;
      border-radius: 999px;
      background: linear-gradient(90deg, rgba(22, 183, 211, 0.92), rgba(143, 85, 54, 0.72));
    }

    .balance-success-stories-eyebrow {
      display: inline-flex;
      align-items: center;
      width: fit-content;
      padding: 0.85rem 1.4rem;
      border-radius: 999px;
      border: 1px solid rgba(22, 183, 211, 0.18);
      background: rgba(255, 255, 255, 0.72);
      color: #0f9fb8;
      font-size: 1.2rem;
      font-weight: 700;
      letter-spacing: 0.14em;
      text-transform: uppercase;
    }

    .balance-success-stories-hero h1 {
      margin: 0;
      color: #17312f;
      font-size: clamp(3.6rem, 6vw, 7rem);
      line-height: 0.94;
      letter-spacing: -0.04em;
      text-wrap: balance;
    }

    .balance-success-stories-copy {
      display: grid;
      gap: 1rem;
      max-width: 94rem;
      color: rgba(23, 49, 47, 0.78);
      font-size: 1.7rem;
      line-height: 1.84;
    }

    .balance-success-stories-platforms {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      padding-top: 0.4rem;
    }

    .balance-success-stories-platform {
      display: inline-flex;
      align-items: center;
      padding: 0.95rem 1.35rem;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.92);
      border: 1px solid rgba(23, 49, 47, 0.08);
      color: #17312f;
      font-size: 1.38rem;
      font-weight: 700;
      line-height: 1.3;
      text-decoration: none;
      transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease;
    }

    .balance-success-stories-platform:hover,
    .balance-success-stories-platform:focus-visible {
      transform: translateY(-1px);
      border-color: rgba(22, 183, 211, 0.22);
      box-shadow: 0 12px 26px rgba(23, 49, 47, 0.08);
    }

    .balance-success-stories-section {
      display: grid;
      gap: 1.6rem;
    }

    .balance-success-stories-section-heading {
      margin: 0;
      color: #17312f;
      font-size: clamp(2.8rem, 4vw, 4.6rem);
      line-height: 0.98;
      letter-spacing: -0.03em;
      text-wrap: balance;
    }

    .balance-success-stories-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(27rem, 1fr));
      gap: 1.95rem;
    }

    .balance-success-stories-card {
      display: grid;
      gap: 1.15rem;
      align-content: start;
      min-height: 100%;
      padding: 1.9rem 1.8rem;
      border-radius: 2.4rem;
      background:
        radial-gradient(circle at top right, rgba(22, 183, 211, 0.08), transparent 32%),
        linear-gradient(180deg, #fffdfa 0%, #f7f4ee 100%);
      border: 1px solid rgba(23, 49, 47, 0.08);
      box-shadow: 0 14px 32px rgba(23, 49, 47, 0.05);
    }

    .balance-success-stories-card::before {
      content: '';
      width: 6.4rem;
      height: 0.22rem;
      border-radius: 999px;
      background: linear-gradient(90deg, rgba(22, 183, 211, 0.92), rgba(143, 85, 54, 0.72));
    }

    .balance-success-stories-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 0.9rem;
      align-items: center;
    }

    .balance-success-stories-stars {
      color: #b87b26;
      font-size: 1.4rem;
      letter-spacing: 0.12em;
    }

    .balance-success-stories-source {
      color: #8f5536;
      font-size: 1.18rem;
      font-weight: 700;
      line-height: 1.45;
      letter-spacing: 0.12em;
      text-transform: uppercase;
    }

    .balance-success-stories-card h3 {
      margin: 0;
      color: #17312f;
      font-size: 1.9rem;
      line-height: 1.14;
      letter-spacing: -0.02em;
      text-wrap: balance;
    }

    .balance-success-stories-card p {
      margin: 0;
      color: rgba(23, 49, 47, 0.82);
      font-size: 1.68rem;
      line-height: 1.84;
    }

    .balance-success-stories-outcome {
      padding: 1.35rem 1.4rem;
      border-radius: 1.6rem;
      background: rgba(22, 183, 211, 0.09);
      border: 1px solid rgba(22, 183, 211, 0.12);
      color: #18504c;
      font-size: 1.45rem;
      font-weight: 700;
      line-height: 1.78;
    }

    .balance-success-stories-link {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      width: fit-content;
      color: #18504c;
      font-size: 1.45rem;
      font-weight: 700;
      line-height: 1.4;
      text-decoration: none;
    }

    .balance-success-stories-link:hover,
    .balance-success-stories-link:focus-visible {
      color: #0f9fb8;
    }

    .balance-success-stories-cta {
      display: grid;
      gap: 1.35rem;
      padding: 2rem 1.9rem;
      border-radius: 2.4rem;
      background: linear-gradient(180deg, rgba(240, 249, 250, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%);
      border: 1px solid rgba(22, 183, 211, 0.16);
      box-shadow: 0 16px 36px rgba(23, 49, 47, 0.05);
    }

    .balance-success-stories-cta h2 {
      margin: 0;
      color: #17312f;
      font-size: clamp(2.5rem, 3.4vw, 3.6rem);
      line-height: 1;
      letter-spacing: -0.03em;
    }

    .balance-success-stories-cta p {
      margin: 0;
      color: rgba(23, 49, 47, 0.8);
      font-size: 1.58rem;
      line-height: 1.8;
      max-width: 80rem;
    }

    .balance-success-stories-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
    }

    .balance-success-stories-button {
      display: inline-flex;
      justify-content: center;
      align-items: center;
      min-height: 5rem;
      padding: 0.9rem 1.8rem;
      border-radius: 999px;
      border: 1px solid transparent;
      font-size: 1.48rem;
      font-weight: 700;
      line-height: 1.25;
      text-decoration: none;
      transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease;
    }

    .balance-success-stories-button--primary {
      background: rgb(22, 183, 211);
      color: #fff;
      box-shadow: 0 12px 24px rgba(22, 183, 211, 0.22);
    }

    .balance-success-stories-button--secondary {
      background: rgba(255, 255, 255, 0.96);
      color: #17312f;
      border-color: rgba(23, 49, 47, 0.1);
    }

    .balance-success-stories-button:hover,
    .balance-success-stories-button:focus-visible {
      transform: translateY(-1px);
      box-shadow: 0 14px 28px rgba(23, 49, 47, 0.1);
    }

    @media screen and (max-width: 749px) {
      .balance-success-stories-page {
        gap: 1.8rem;
      }

      .balance-success-stories-hero,
      .balance-success-stories-card,
      .balance-success-stories-cta {
        padding: 1.8rem 1.45rem;
        border-radius: 2rem;
      }

      .balance-success-stories-platforms,
      .balance-success-stories-actions {
        display: grid;
      }

      .balance-success-stories-platform,
      .balance-success-stories-button {
        width: 100%;
      }

      .balance-success-stories-card h3 {
        font-size: 1.74rem;
      }
    }
  `;

  document.head.appendChild(style);
}

function enhancePatientSuccessStoriesPage() {
  if (window.location.pathname !== '/pages/patient-success-stories') return;
  if (document.body.dataset.balanceSuccessStoriesEnhanced === 'true') return;
  if (document.querySelector('.balance-clinic-hero')) return;

  const prose = document.querySelector('.balance-main-page__prose.rte');
  const pageTitle = document.querySelector('.main-page-title');

  if (!prose || !prose.querySelector('h2') || !prose.querySelector('h3')) return;

  injectPatientSuccessStoriesStyles();

  const children = Array.from(prose.children);
  const introNodes = [];
  const sections = [];
  let currentSection = null;
  let currentStory = null;

  const finalizeStory = () => {
    if (!currentSection || !currentStory) return;
    currentSection.stories.push(currentStory);
    currentStory = null;
  };

  const finalizeSection = () => {
    finalizeStory();
    if (!currentSection) return;
    sections.push(currentSection);
    currentSection = null;
  };

  children.forEach((child) => {
    const tagName = child.tagName ? child.tagName.toUpperCase() : '';
    const text = child.textContent.trim();

    if (tagName === 'H2') {
      finalizeSection();
      currentSection = { title: text, stories: [] };
      return;
    }

    if (tagName === 'H3' && currentSection) {
      finalizeStory();
      currentStory = { title: text, source: '', story: [], outcome: '' };
      return;
    }

    if (!currentSection) {
      introNodes.push(child.cloneNode(true));
      return;
    }

    if (tagName === 'P' && currentStory) {
      const normalized = text.toLowerCase();
      if (normalized.startsWith('source:')) {
        currentStory.source = text.replace(/^source:\s*/i, '');
        return;
      }
      if (normalized.startsWith('outcome:')) {
        currentStory.outcome = text.replace(/^outcome:\s*/i, '');
        return;
      }
      currentStory.story.push(text);
    }
  });

  finalizeSection();

  if (!sections.length) return;

  const defaultHeroTitle = pageTitle?.textContent.trim() || 'Patient success stories';
  let heroTitle = defaultHeroTitle;
  const introParagraphs = [];
  const platformLinks = [];

  introNodes.forEach((node) => {
    const tagName = node.tagName ? node.tagName.toUpperCase() : '';
    const text = node.textContent.trim();
    if (!text) return;

    if (tagName === 'P' && node.querySelector('strong') && introParagraphs.length === 0) {
      heroTitle = node.textContent.trim();
      return;
    }

    if (tagName === 'P' && node.querySelector('a')) {
      platformLinks.push(
        ...Array.from(node.querySelectorAll('a')).map((link) => ({
          label: link.textContent.trim(),
          href: link.href,
        }))
      );
      return;
    }

    introParagraphs.push(text);
  });

  const sectionLinkMap = {
    fertility: '/pages/fertility-acupuncture-santa-monica',
    women: '/pages/womens-health-acupuncture-santa-monica',
    pain: '/pages/pain-relief-acupuncture-santa-monica',
  };

  const page = document.createElement('div');
  page.className = 'balance-success-stories-page';

  const hero = document.createElement('section');
  hero.className = 'balance-success-stories-hero';
  hero.innerHTML = `
    <span class="balance-success-stories-eyebrow">Public review stories</span>
    <h1>${heroTitle}</h1>
    <div class="balance-success-stories-copy"></div>
    <div class="balance-success-stories-platforms"></div>
  `;

  const copy = hero.querySelector('.balance-success-stories-copy');
  introParagraphs.forEach((paragraph) => {
    const node = document.createElement('p');
    node.textContent = paragraph;
    copy.appendChild(node);
  });

  const platforms = hero.querySelector('.balance-success-stories-platforms');
  platformLinks.forEach(({ label, href }) => {
    const link = document.createElement('a');
    link.className = 'balance-success-stories-platform';
    link.href = href;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = label;
    platforms.appendChild(link);
  });

  page.appendChild(hero);

  sections.forEach((section) => {
    const sectionEl = document.createElement('section');
    sectionEl.className = 'balance-success-stories-section';

    const heading = document.createElement('h2');
    heading.className = 'balance-success-stories-section-heading';
    heading.textContent = section.title;
    sectionEl.appendChild(heading);

    const grid = document.createElement('div');
    grid.className = 'balance-success-stories-grid';

    const sectionKey = Object.keys(sectionLinkMap).find((key) => section.title.toLowerCase().includes(key));
    const destination = sectionKey ? sectionLinkMap[sectionKey] : '/pages/book-appointment';

    section.stories.forEach((story) => {
      const card = document.createElement('article');
      card.className = 'balance-success-stories-card';

      const meta = document.createElement('div');
      meta.className = 'balance-success-stories-meta';

      const stars = document.createElement('span');
      stars.className = 'balance-success-stories-stars';
      stars.textContent = '★★★★★';
      meta.appendChild(stars);

      if (story.source) {
        const source = document.createElement('span');
        source.className = 'balance-success-stories-source';
        source.textContent = story.source;
        meta.appendChild(source);
      }

      const title = document.createElement('h3');
      title.textContent = story.title;

      const body = document.createElement('p');
      body.textContent = story.story.join(' ');

      card.appendChild(meta);
      card.appendChild(title);
      card.appendChild(body);

      if (story.outcome) {
        const outcome = document.createElement('div');
        outcome.className = 'balance-success-stories-outcome';
        outcome.textContent = story.outcome;
        card.appendChild(outcome);
      }

      const link = document.createElement('a');
      link.className = 'balance-success-stories-link';
      link.href = destination;
      link.textContent = 'See related care →';
      card.appendChild(link);

      grid.appendChild(card);
    });

    sectionEl.appendChild(grid);
    page.appendChild(sectionEl);
  });

  const cta = document.createElement('section');
  cta.className = 'balance-success-stories-cta';
  cta.innerHTML = `
    <h2>Ready to start your own story?</h2>
    <p>Book online if you already know where to begin, or contact the clinic first if you want help deciding whether fertility, women’s health, pain relief, or a more general first visit makes the most sense.</p>
    <div class="balance-success-stories-actions">
      <a class="balance-success-stories-button balance-success-stories-button--primary" href="/pages/book-appointment">Book Appointment</a>
      <a class="balance-success-stories-button balance-success-stories-button--secondary" href="/pages/contact">Contact the Clinic</a>
    </div>
  `;
  page.appendChild(cta);

  prose.innerHTML = '';
  prose.appendChild(page);
  prose.classList.add('balance-success-stories-enhanced');

  if (pageTitle) {
    pageTitle.classList.add('balance-success-stories-title-hidden');
  }

  document.body.dataset.balanceSuccessStoriesEnhanced = 'true';
}

function normalizeDrWangTrainingCopy() {
  if (
    !window.location.pathname.includes('/pages/dr-ping-wang') &&
    !window.location.pathname.includes('/pages/doctors')
  ) {
    return;
  }

  const applyHeroBodyCopy = () => {
    const heroBody = document.querySelector('.balance-clinic-hero__body');
    if (!heroBody) return;

    if (
      heroBody.textContent.includes('UCLA East-West clinician') ||
      heroBody.textContent.includes('25 years of clinical experience')
    ) {
      heroBody.textContent =
        'Women’s wellness, fertility support, pain relief, sleep, and digestion care with 25+ years of clinical experience and UCLA East-West Medicine Clinician background.';
    }
  };

  const factHeader = document.querySelector('.balance-fact-grid__header');
  const factHeading = factHeader?.querySelector('h2');
  const factBody = factHeader?.querySelector('.balance-fact-grid__body');

  applyHeroBodyCopy();
  window.setTimeout(applyHeroBodyCopy, 150);
  window.setTimeout(applyHeroBodyCopy, 600);
  window.setTimeout(applyHeroBodyCopy, 1800);

  if (factHeading && factHeading.textContent.trim() === 'Why patients trust this clinic.') {
    factBody.textContent =
      'Personalized care rooted in Traditional Chinese Medicine, modern science, and careful diagnostic differentiation rather than symptom-only treatment.';
  }

  document.querySelectorAll('.balance-fact-grid__card').forEach((card) => {
    const eyebrow = card.querySelector('.balance-fact-grid__card-tag')?.textContent.trim().toLowerCase();
    const title = card.querySelector('.balance-fact-grid__card-title');
    const text = card.querySelector('.balance-fact-grid__card-text');

    if (!eyebrow || !title || !text) return;

    if (eyebrow === 'experience') {
      title.textContent = '25+ years of clinical experience';
      text.textContent =
        'More than 25 years of clinical experience in Traditional Chinese Medicine and acupuncture care.';
    }

    if (eyebrow === 'training') {
      title.textContent = 'Traditional Chinese Medicine and modern science';
      text.textContent =
        'Classical Chinese medicine training, research experience, and work connected to UCLA East-West Medicine shape a careful, individualized treatment style.';
    }

    if (eyebrow === 'current role') {
      title.textContent = 'Dr. Ping Wang, PhD, OMD, L.Ac.';
      text.textContent =
        'Founder of Balance Healthcare, with ongoing work connected to UCLA Center for East-West Medicine.';
    }

    if (eyebrow === 'specialties') {
      title.textContent = 'Women’s wellness, fertility support, pain relief, and complex symptom patterns';
      text.textContent =
        'Patients also seek her care for sleep support, digestion, stress-related symptoms, healthy aging, and facial acupuncture.';
    }
  });

  document.querySelectorAll('.balance-clinic-hero__note').forEach((note) => {
    const title = note.querySelector('.balance-clinic-hero__note-title');
    const text = note.querySelector('.balance-clinic-hero__note-text');
    if (!title || !text) return;

    const normalizedTitle = title.textContent.trim().toLowerCase();

    if (normalizedTitle === '25+ years of practice') {
      title.textContent = '25+ years of clinical experience';
      text.textContent =
        'More than 25 years of clinical experience in Traditional Chinese Medicine and acupuncture care.';
    }

    if (
      normalizedTitle === 'east meets west' ||
      normalizedTitle === 'traditional and medical training' ||
      normalizedTitle === 'traditional chinese medicine and modern science'
    ) {
      title.textContent = 'Traditional Chinese Medicine and modern science';
      text.textContent =
        'Classical Chinese medicine, modern biomedical science, and individualized treatment planning shaped around the whole patient.';
    }
  });
}

function ensureLiveTcmWayCardImage() {
  if (!window.location.pathname.includes('/pages/apps')) return;

  const fallbackImage =
    'https://cdn.shopify.com/s/files/1/0572/5532/9981/t/9/assets/balance-card-live-the-tcm-way.jpg?v=1774905225';

  document.querySelectorAll('.balance-care-lanes__card').forEach((card) => {
    const title = card.querySelector('.balance-care-lanes__card-title');
    const media = card.querySelector('.balance-care-lanes__media');

    if (!title || !media) return;
    if (!title.textContent || !title.textContent.includes('Live the TCM Way')) return;
    if (media.querySelector('img')) return;

    const image = document.createElement('img');
    image.src = fallbackImage;
    image.alt = 'Live the TCM Way app on the App Store';
    image.loading = 'lazy';
    image.width = 960;
    image.height = 632;
    media.appendChild(image);
  });
}

function normalizeArticlesMenuLabels() {
  const normalizeList = (list, itemSelector) => {
    if (!list) return;

    const links = Array.from(list.querySelectorAll(itemSelector));
    if (!links.length) return;

    const firstVisitLink = links.find((link) => {
      const href = link.getAttribute('href') || '';
      return href.includes('/pages/your-first-acupuncture-visit');
    });

    const moreArticlesLink = links.find((link) => {
      const href = link.getAttribute('href') || '';
      const label = (link.textContent || '').trim().toLowerCase();
      return href.includes('/pages/resources') || href.includes('/blogs/') || label === 'all articles';
    });

    if (firstVisitLink) {
      firstVisitLink.textContent = 'Your First Acupuncture Visit';
      const item = firstVisitLink.closest('li');
      if (item && item.parentNode) item.parentNode.insertBefore(item, item.parentNode.firstChild);
    }

    if (moreArticlesLink) {
      moreArticlesLink.textContent = 'More Articles';
      const item = moreArticlesLink.closest('li');
      if (item && item.parentNode) item.parentNode.appendChild(item);
    }
  };

  document.querySelectorAll('summary#HeaderMenu-articles').forEach((summary) => {
    const details = summary.closest('details');
    if (!details) return;
    normalizeList(details.querySelector('.header__submenu'), 'a.header__menu-item');
    normalizeList(details.querySelector('.mega-menu__list'), 'a.mega-menu__link--level-2');
  });

  document.querySelectorAll('.menu-drawer__menu-group').forEach((group) => {
    const label = group.querySelector('.menu-drawer__menu-label');
    if (!label || label.textContent.trim().toLowerCase() !== 'articles') return;
    normalizeList(group.querySelector('.menu-drawer__group-list'), 'a.menu-drawer__menu-item--child');
  });
}

function removeLiquidSyntaxErrorText() {
  const containers = document.querySelectorAll('.header-wrapper, sticky-header, header.header');
  containers.forEach((container) => {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const textNodes = [];

    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (node.textContent && node.textContent.includes('Liquid syntax error')) {
        textNodes.push(node);
      }
    }

    textNodes.forEach((node) => {
      node.textContent = node.textContent.replace(/Liquid syntax error[\s\S]*?(?=(Home|Clinic|Shop|Articles|Apps|Contact|$))/g, '');
    });
  });
}

function alignArticlePageShellWidth() {
  document.querySelectorAll('.balance-article-page').forEach((page) => {
    const hero = page.querySelector('.balance-article-page__hero');
    const shell = page.querySelector('.balance-article-page__shell');

    if (!shell) return;

    shell.style.maxWidth = '118rem';
    shell.style.width = '100%';

    if (!hero) return;

    const heroWidth = Math.round(hero.getBoundingClientRect().width);
    if (!heroWidth) return;

    shell.style.maxWidth = `${heroWidth}px`;
  });
}

function brightenHomepageProofStripTitles() {
  document.querySelectorAll('.balance-proof-strip__label').forEach((label) => {
    label.style.color = 'rgba(240, 248, 244, 0.95)';
  });

  document.querySelectorAll('.balance-proof-strip__title').forEach((title) => {
    title.style.color = '#f7fffc';
    title.style.textShadow = '0 1px 10px rgba(0, 0, 0, 0.08)';
  });
}

function normalizeClinicParkingCopy() {
  const replacements = new Map([
    [
      'Santa Monica, CA 90404. Free street parking available on side streets.',
      'Santa Monica, CA 90404. Metered parking is available nearby, and free parking is available behind the building.',
    ],
    [
      'Free street parking on side streets. The clinic is also accessible by Big Blue Bus and Metro Expo Line.',
      'Metered parking is available on Santa Monica Blvd and nearby streets, and there is free parking behind the building. The clinic is also accessible by Big Blue Bus and Metro Expo Line.',
    ],
  ]);

  document.querySelectorAll('.balance-clinic-access__card-text, .balance-first-visit-guide__details').forEach((node) => {
    if (!node.textContent) return;

    replacements.forEach((replacement, stale) => {
      if (!node.textContent.includes(stale)) return;
      node.textContent = node.textContent.replace(stale, replacement);
    });
  });
}

function ensureMobileHeaderDrawerTapTarget() {
  if (window.innerWidth >= 990) return;

  document.querySelectorAll('header-drawer').forEach((drawer) => {
    drawer.style.gridArea = 'left-icons';
    drawer.style.position = 'relative';
    drawer.style.zIndex = '35';
    drawer.style.pointerEvents = 'auto';
    drawer.style.overflow = 'visible';

    const container = drawer.querySelector('.menu-drawer-container');
    if (container) {
      container.style.position = 'static';
      container.style.zIndex = 'auto';
      container.style.pointerEvents = 'auto';
      container.style.overflow = 'visible';
      container.style.width = 'auto';
    }

    const summary = drawer.querySelector('summary.header__icon--menu');
    const details = drawer.querySelector('details.menu-drawer-container');
    if (!summary) return;

    summary.style.position = 'relative';
    summary.style.zIndex = '36';
    summary.style.pointerEvents = 'auto';
    summary.style.touchAction = 'manipulation';

    if (summary.dataset.balanceMenuTouchBound === 'true') return;
    summary.dataset.balanceMenuTouchBound = 'true';

    summary.addEventListener(
      'touchstart',
      () => {
        ensureMobileHeaderDrawerTapTarget();
      },
      { passive: true },
    );

    summary.addEventListener(
      'touchend',
      (event) => {
        const currentDetails = summary.closest('details');
        if (!currentDetails || currentDetails.hasAttribute('open')) return;
        event.preventDefault();
        summary.click();
      },
      { passive: false },
    );

    if (details) {
      details.style.overflow = 'visible';
    }

    const menuDrawer = drawer.querySelector('.menu-drawer');
    if (menuDrawer) {
      menuDrawer.style.position = 'fixed';
      menuDrawer.style.left = '0';
      menuDrawer.style.top = 'var(--header-bottom-position, 0px)';
      menuDrawer.style.width = 'min(88vw, 38rem)';
      menuDrawer.style.maxWidth = '88vw';
      menuDrawer.style.minWidth = 'min(88vw, 38rem)';
      menuDrawer.style.height = 'calc(100dvh - var(--header-bottom-position, 0px))';
      menuDrawer.style.maxHeight = 'calc(100dvh - var(--header-bottom-position, 0px))';
      menuDrawer.style.zIndex = '37';
      menuDrawer.style.transition = 'none';
      menuDrawer.style.overflow = 'hidden';
    }
  });
}

onBalanceDocumentReady(enhancePatientSuccessStoriesPage);
document.addEventListener('shopify:section:load', enhancePatientSuccessStoriesPage);
onBalanceDocumentReady(normalizeDrWangTrainingCopy);
document.addEventListener('shopify:section:load', normalizeDrWangTrainingCopy);
onBalanceDocumentReady(ensureLiveTcmWayCardImage);
document.addEventListener('shopify:section:load', ensureLiveTcmWayCardImage);
onBalanceDocumentReady(normalizeArticlesMenuLabels);
document.addEventListener('shopify:section:load', normalizeArticlesMenuLabels);
onBalanceDocumentReady(removeLiquidSyntaxErrorText);
document.addEventListener('shopify:section:load', removeLiquidSyntaxErrorText);
onBalanceDocumentReady(ensureHomepageHeroEyebrow);
document.addEventListener('shopify:section:load', ensureHomepageHeroEyebrow);
onBalanceDocumentReady(alignArticlePageShellWidth);
document.addEventListener('shopify:section:load', alignArticlePageShellWidth);
onBalanceDocumentReady(brightenHomepageProofStripTitles);
document.addEventListener('shopify:section:load', brightenHomepageProofStripTitles);
onBalanceDocumentReady(normalizeClinicParkingCopy);
document.addEventListener('shopify:section:load', normalizeClinicParkingCopy);
onBalanceDocumentReady(ensureMobileHeaderDrawerTapTarget);
document.addEventListener('shopify:section:load', ensureMobileHeaderDrawerTapTarget);
onBalanceDocumentReady(redirectAppointmentShortlink);

(function(){if(window.balanceContactLinkEnhancerLoaded)return;window.balanceContactLinkEnhancerLoaded=true;var mapBase='https://www.google.com/maps/search/?api=1&query=';var items=[{kind:'address',label:'Open directions to Balance Healthcare Santa Monica',href:mapBase+encodeURIComponent('2812 Santa Monica Blvd Suite 208 Santa Monica CA 90404'),variants:['2812 Santa Monica Blvd, Suite 208, Santa Monica, CA 90404','2812 Santa Monica Blvd, Suite 208']},{kind:'address',label:'Open directions to UCLA Center for East-West Medicine',href:mapBase+encodeURIComponent('2336 Santa Monica Blvd Suite 301 Santa Monica CA 90404'),variants:['2336 Santa Monica Blvd, Suite 301, Santa Monica, CA 90404','2336 Santa Monica Blvd, Suite 301']},{kind:'phone',label:'Call Balance Healthcare at 818-918-3238',href:'tel:+18189183238',variants:['+1 (818) 918-3238','(818) 918-3238','818-918-3238']},{kind:'phone',label:'Call or text Balance Healthcare at 818-659-5866',href:'tel:+18186595866',variants:['+1 (818) 659-5866','(818) 659-5866','818-659-5866']},{kind:'phone',label:'Call UCLA Center for East-West Medicine at 310-998-9118',href:'tel:+13109989118',variants:['+1 (310) 998-9118','(310) 998-9118','310-998-9118']}];var rules=[];items.forEach(function(item){item.variants.forEach(function(text){rules.push({text:text,href:item.href,kind:item.kind,label:item.label});});});rules.sort(function(a,b){return b.text.length-a.text.length;});function addStyles(){if(document.getElementById('balance-contact-link-styles'))return;var style=document.createElement('style');style.id='balance-contact-link-styles';style.textContent='.balance-contact-link{color:inherit;text-decoration:underline;text-underline-offset:.14em;text-decoration-thickness:.08em;-webkit-touch-callout:default;touch-action:manipulation}.balance-contact-link:focus-visible{outline:2px solid currentColor;outline-offset:2px}';document.head.appendChild(style);}function shouldSkip(node){var parent=node.parentElement;if(!parent)return true;return !!parent.closest('a,button,script,style,noscript,textarea,input,select,option,[contenteditable="true"],.no-contact-auto-link');}function findNext(text,start){var best=null;rules.forEach(function(rule){var index=text.indexOf(rule.text,start);if(index===-1)return;if(!best||index<best.index||index===best.index&&rule.text.length>best.text.length)best={index:index,text:rule.text,href:rule.href,kind:rule.kind,label:rule.label};});return best;}function linkifyTextNode(node){var text=node.nodeValue,fragment=document.createDocumentFragment(),cursor=0,match;while((match=findNext(text,cursor))){if(match.index>cursor)fragment.appendChild(document.createTextNode(text.slice(cursor,match.index)));var link=document.createElement('a');link.className='balance-contact-link balance-contact-link--'+match.kind;link.href=match.href;link.textContent=match.text;link.setAttribute('aria-label',match.label);link.title=match.label;if(match.kind==='address'){link.target='_blank';link.rel='noopener noreferrer';}fragment.appendChild(link);cursor=match.index+match.text.length;}if(cursor===0)return;if(cursor<text.length)fragment.appendChild(document.createTextNode(text.slice(cursor)));node.parentNode.replaceChild(fragment,node);}function enhanceContactLinks(root){root=root||document.body;if(!root)return;addStyles();var nodes=[],walker=document.createTreeWalker(root,NodeFilter.SHOW_TEXT,{acceptNode:function(node){if(!node.nodeValue||!node.nodeValue.trim()||shouldSkip(node))return NodeFilter.FILTER_REJECT;return rules.some(function(rule){return node.nodeValue.indexOf(rule.text)!==-1;})?NodeFilter.FILTER_ACCEPT:NodeFilter.FILTER_REJECT;}});while(walker.nextNode())nodes.push(walker.currentNode);nodes.forEach(linkifyTextNode);}function run(){enhanceContactLinks(document.body);}if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',run);else run();document.addEventListener('shopify:section:load',function(event){enhanceContactLinks(event.target);});})();
